import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Expand, RefreshCw, Scale, Search, ShieldCheck } from "lucide-react";

import { Button } from "../../components/common/UI";
import { apiFetch, useApp } from "../../context/AppContext";
import { confirmAction } from "../../utils/sweetAlert";

const GAP_BADGE = "bg-sky-50 text-sky-700 ring-sky-100";

function countryKey(entry) {
  return `${entry.region}||${entry.country}`;
}

function questionKey(question) {
  return `${question.module}:${question.id}`;
}

function linkCount(entry) {
  return Object.values(entry?.links || {}).reduce((sum, list) => sum + (list || []).length, 0);
}

function mappingLinksPayload(entry) {
  const links = [];
  Object.entries(entry.links || {}).forEach(([checklistItemId, list]) => {
    const bySection = new Map();
    (list || []).filter((question) => question.module === "gap").forEach((question) => {
      const section = question.section || "";
      if (!section) return;
      if (!bySection.has(section)) bySection.set(section, []);
      bySection.get(section).push({
        module: "gap",
        id: question.id,
        title: question.title,
        section,
        ref: question.ref || "",
      });
    });
    bySection.forEach((questions, section) => {
      if (questions.length) {
        links.push({ checklist_item_id: checklistItemId, section, questions });
      }
    });
  });
  return links;
}

function saveSummary(entries) {
  const clearing = entries.filter((entry) => linkCount(entry) === 0);
  const replacing = entries.filter((entry) => entry.saved && linkCount(entry) > 0);
  const creating = entries.filter((entry) => !entry.saved && linkCount(entry) > 0);
  const parts = [];
  if (creating.length) parts.push(`${creating.map((entry) => entry.country).join(", ")} will be saved.`);
  if (replacing.length) parts.push(`${replacing.map((entry) => entry.country).join(", ")} will replace the saved mapping.`);
  if (clearing.length) parts.push(`${clearing.map((entry) => entry.country).join(", ")} will be cleared. Users will no longer see those questions.`);
  return parts.join(" ");
}

function itemSection(item) {
  const category = String(item.category || "").trim();
  const code = String(item.code || "").trim();
  if (category && category.toLowerCase() !== "general") return category;
  if (code) return code;
  return "General";
}

function officialRef(item) {
  const regulation = String(item.regulation || "").trim();
  if (regulation) return regulation;
  const code = String(item.code || "").trim();
  const section = itemSection(item);
  if (code && code !== section) return code;
  return "";
}

function sectionsOf(items) {
  const names = [];
  items.forEach((item) => {
    const name = itemSection(item);
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
}

export default function LawQuestionMappingStudio() {
  const navigate = useNavigate();
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [dpdpSection, setDpdpSection] = useState("");
  const [masterId, setMasterId] = useState("");
  const [countries, setCountries] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [dpdpQuery, setDpdpQuery] = useState("");
  const [countryQuery, setCountryQuery] = useState({});
  const [hint, setHint] = useState("");
  const [reading, setReading] = useState(null);
  const [countryPage, setCountryPage] = useState(0);
  const goBack = () => {
    if (!saving) navigate("/tenant/compliance-checklists");
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setConfirming(false);
      setMasterId("");
      try {
        const listRes = await apiFetch("/tenant/compliance-checklists");
        const listData = await listRes.json().catch(() => ({}));
        if (!listRes.ok) throw new Error(listData.detail || "Unable to load checklists");
        const sources = (listData.checklists || []).filter((entry) => entry.item_count || entry.law_mapped);
        const bankRes = await apiFetch("/tenant/compliance-checklists/question-bank");
        const bankData = await bankRes.json().catch(() => ({}));
        if (!bankRes.ok) throw new Error(bankData.detail || "Unable to load DPDP questions");
        const gapQuestions = (bankData.modules || [])
          .flatMap((module) => module.questions || [])
          .filter((question) => question.module === "gap");

        const loaded = await Promise.all(sources.map(async (entry) => {
          const params = `region=${encodeURIComponent(entry.region)}&country=${encodeURIComponent(entry.country)}`;
          const [itemRes, mapRes] = await Promise.all([
            apiFetch(`/tenant/compliance-checklists/items?${params}`),
            apiFetch(`/tenant/compliance-checklists/law-mapping?${params}`),
          ]);
          const itemData = await itemRes.json().catch(() => ({}));
          const mapData = await mapRes.json().catch(() => ({}));
          if (!itemRes.ok) throw new Error(itemData.detail || `Unable to load ${entry.country}`);
          const links = {};
          (mapData.links || []).forEach((link) => {
            const mapped = (link.questions || []).filter((question) => question.module === "gap");
            if (!mapped.length) return;
            const current = links[link.checklist_item_id] || [];
            const seen = new Set(current.map((question) => questionKey(question)));
            mapped.forEach((question) => {
              const key = questionKey(question);
              if (seen.has(key)) return;
              seen.add(key);
              current.push(question);
            });
            links[link.checklist_item_id] = current;
          });
          const items = itemData.items || [];
          const names = sectionsOf(items);
          return {
            key: countryKey(entry),
            region: entry.region,
            country: entry.country,
            locked: Boolean(mapData.mapped),
            saved: Boolean(mapData.mapped),
            lawName: mapData.law_name || itemData.regulation || entry.regulation || "",
            officialName: mapData.official_name || "",
            items,
            section: "",
            sections: names,
            links,
          };
        }));

        if (cancelled) return;
        setQuestions(gapQuestions);
        setCountries(loaded);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || "Unable to open mapping");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const dpdpSections = useMemo(() => {
    const names = [];
    questions.forEach((question) => {
      if (question.section && !names.includes(question.section)) names.push(question.section);
    });
    return names;
  }, [questions]);

  const visibleDpdp = useMemo(() => {
    const source = dpdpSection
      ? questions.filter((question) => question.section === dpdpSection)
      : questions;
    const query = dpdpQuery.trim().toLowerCase();
    if (!query) return source;
    return source.filter((question) => (
      `${question.section || ""} ${question.ref || ""} ${question.title || ""}`.toLowerCase().includes(query)
    ));
  }, [questions, dpdpSection, dpdpQuery]);
  const mappedMasterCounts = useMemo(() => {
    const counts = {};
    countries.forEach((entry) => {
      Object.values(entry.links || {}).forEach((list) => {
        (list || []).forEach((question) => {
          if (question.module !== "gap") return;
          const key = questionKey(question);
          counts[key] = (counts[key] || 0) + 1;
        });
      });
    });
    return counts;
  }, [countries]);
  const master = questions.find((question) => questionKey(question) === masterId) || null;
  const countriesToSave = countries.filter((entry) => {
    if (entry.locked) return false;
    if (linkCount(entry) > 0) return true;
    return Boolean(entry.saved);
  });
  const linkedCount = countries.reduce(
    (sum, entry) => sum + Object.values(entry.links).reduce((inner, list) => inner + list.length, 0),
    0,
  );
  const pageSize = 2;
  const pageCount = Math.max(1, Math.ceil(countries.length / pageSize));
  const safePage = Math.min(countryPage, pageCount - 1);
  const shownCountries = countries.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const shownKeys = shownCountries.map((entry) => entry.key);

  const focusCountry = (key) => {
    const index = countries.findIndex((entry) => entry.key === key);
    if (index >= 0) setCountryPage(Math.floor(index / pageSize));
  };

  const startRemap = (key) => {
    setCountries((current) => current.map((entry) => (
      entry.key === key ? { ...entry, locked: false } : entry
    )));
    setReading((current) => (
      current && current.countryKey === key ? { ...current, locked: false } : current
    ));
    setHint("");
  };

  const openCountryReading = (entry, item) => {
    setReading({
      kind: "country",
      countryKey: entry.key,
      itemId: item.id,
      country: entry.country,
      title: item.title,
      ref: officialRef(item),
      section: itemSection(item),
      category: item.category || "",
      code: item.code || "",
      notes: item.notes || "",
      evidence: item.evidence || "",
      owner: item.owner || "",
      priority: item.priority || "",
      status: item.status || "",
      locked: entry.locked,
      mappedQuestions: entry.links[item.id] || [],
    });
  };

  const setCountrySection = (key, section) => {
    setCountries((current) => current.map((entry) => (
      entry.key === key ? { ...entry, section } : entry
    )));
  };

  const setCountryField = (key, field, value) => {
    setCountries((current) => current.map((entry) => (
      entry.key === key ? { ...entry, [field]: value } : entry
    )));
  };

  const isLinked = (entry, item) => (
    master && (entry.links[item.id] || []).some((question) => questionKey(question) === questionKey(master))
  );

  const toggleMatch = (country, item) => {
    if (country.locked) return;
    if (!master) {
      setHint("Select a DPDP question on the left, then tick the matching country question.");
      return;
    }
    setHint("");
    setCountries((current) => current.map((entry) => {
      if (entry.key !== country.key) return entry;
      const all = entry.links[item.id] || [];
      const others = all.filter((question) => question.section !== master.section);
      const same = all.filter((question) => question.section === master.section);
      const has = same.some((question) => questionKey(question) === questionKey(master));
      const nextSame = has
        ? same.filter((question) => questionKey(question) !== questionKey(master))
        : [...same, master];
      const next = [...others, ...nextSame];
      const links = { ...entry.links };
      if (next.length) links[item.id] = next;
      else delete links[item.id];
      return { ...entry, links };
    }));
  };

  const clearCountry = async (entry) => {
    const ok = await confirmAction({
      title: `Clear ${entry.country}?`,
      text: "Matched DPDP questions for this country will be removed. Users will no longer see them on the checklist.",
      confirmText: "Clear mapping",
      icon: "warning",
    });
    if (!ok) return;
    if (!entry.saved) {
      setCountries((current) => current.map((item) => (
        item.key === entry.key ? { ...item, links: {} } : item
      )));
      return;
    }
    setSaving(true);
    try {
      const params = `region=${encodeURIComponent(entry.region)}&country=${encodeURIComponent(entry.country)}`;
      const response = await apiFetch(`/tenant/compliance-checklists/law-mapping?${params}`, { method: "DELETE" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || `Unable to clear ${entry.country}`);
      setCountries((current) => current.map((item) => (
        item.key === entry.key ? { ...item, links: {}, locked: false, saved: false } : item
      )));
      setReading((current) => (current?.countryKey === entry.key ? null : current));
      showNotification(`${entry.country} mapping cleared. Users will no longer see those questions.`, "success");
    } catch (clearError) {
      showNotification(clearError.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const pending = countriesToSave;
    if (!pending.length) return;
    try {
      setSaving(true);
      for (const entry of pending) {
        const links = mappingLinksPayload(entry);
        const clearing = links.length === 0;
        const params = `region=${encodeURIComponent(entry.region)}&country=${encodeURIComponent(entry.country)}`;
        const response = clearing
          ? await apiFetch(`/tenant/compliance-checklists/law-mapping?${params}`, { method: "DELETE" })
          : await apiFetch("/tenant/compliance-checklists/law-mapping", {
            method: entry.saved ? "PUT" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              region: entry.region,
              country: entry.country,
              law_name: entry.lawName,
              official_name: entry.officialName,
              links,
            }),
          });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || `Unable to save ${entry.country}`);
        setCountries((current) => current.map((item) => {
          if (item.key !== entry.key) return item;
          if (clearing) return { ...item, links: {}, locked: false, saved: false };
          return { ...item, locked: true, saved: true };
        }));
      }
      setConfirming(false);
      const clearedAny = pending.some((entry) => mappingLinksPayload(entry).length === 0);
      const savedAny = pending.some((entry) => mappingLinksPayload(entry).length > 0);
      showNotification(
        clearedAny && !savedAny
          ? "Mapping cleared. Users will no longer see those questions."
          : clearedAny
            ? "Mapping saved. Cleared countries no longer show on the checklist."
            : "Country laws matched to DPDP. Users can now see them on each checklist.",
        "success",
      );
    } catch (saveError) {
      showNotification(saveError.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = "h-8 min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";
  const readingEntry = reading?.kind === "country" ? countries.find((item) => item.key === reading.countryKey) : null;
  const readingItem = readingEntry?.items.find((item) => item.id === reading?.itemId) || null;
  const readingLinked = Boolean(readingEntry && readingItem && isLinked(readingEntry, readingItem));
  const readingMapped = readingEntry && readingItem
    ? (readingEntry.links[readingItem.id] || [])
    : (reading?.mappedQuestions || []);

  return (
    <div className="relative -m-4 flex h-[calc(100dvh-6.5rem)] flex-col gap-2 overflow-hidden bg-slate-100 p-2 lg:-m-6">
        <div className="flex h-12 shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-2.5 shadow-sm">
          <button
            type="button"
            disabled={saving}
            onClick={goBack}
            title="Back to checklists"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
            <Scale className="h-4 w-4" />
          </div>
          <h1 className="shrink-0 text-sm font-bold text-slate-900">Map to DPDP</h1>
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
            {countries.map((entry) => {
              const onScreen = shownKeys.includes(entry.key);
              const linkedHere = Object.values(entry.links).reduce((sum, list) => sum + list.length, 0);
              return (
                <button
                  key={entry.key}
                  type="button"
                  onClick={() => focusCountry(entry.key)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                    onScreen
                      ? "bg-blue-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700"
                  }`}
                >
                  {entry.country}
                  {entry.locked ? " · Saved" : linkedHere > 0 ? ` · ${linkedHere}` : ""}
                </button>
              );
            })}
          </div>
          {countries.length > pageSize && (
            <div className="flex shrink-0 items-center gap-0.5 rounded-full bg-slate-100 p-0.5">
              <button
                type="button"
                aria-label="Previous countries"
                disabled={safePage === 0}
                onClick={() => setCountryPage((page) => Math.max(0, page - 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-600 hover:bg-white disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-1 text-[11px] font-semibold text-slate-600">
                {safePage * pageSize + 1}–{Math.min(countries.length, safePage * pageSize + pageSize)} of {countries.length}
              </span>
              <button
                type="button"
                aria-label="Next countries"
                disabled={safePage >= pageCount - 1}
                onClick={() => setCountryPage((page) => Math.min(pageCount - 1, page + 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-600 hover:bg-white disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {master && (
            <p className="hidden max-w-[240px] truncate text-[11px] font-medium text-blue-700 xl:block" title={master.title}>
              {master.ref ? `${master.ref} · ` : ""}{master.title}
            </p>
          )}
          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
            {linkedCount} linked
          </span>
          {countries.some((entry) => !entry.locked) && (
            <Button type="button" size="sm" variant="primary" disabled={!countriesToSave.length} onClick={() => setConfirming(true)}>
              Save mapping
            </Button>
          )}
        </div>

        {hint && (
          <p className="shrink-0 rounded-xl bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">{hint}</p>
        )}

        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-slate-500">Loading DPDP and country laws…</div>
        ) : error ? (
          <div className="px-6 py-16 text-center text-sm text-red-600">{error}</div>
        ) : countries.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <ShieldCheck className="h-6 w-6 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Upload a country checklist before mapping.</p>
          </div>
        ) : (
          <div
            className="grid min-h-0 flex-1 gap-3 overflow-hidden"
            style={{ gridTemplateColumns: `repeat(${1 + Math.max(shownCountries.length, 1)}, minmax(0, 1fr))` }}
          >
            <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex h-11 shrink-0 items-center gap-1.5 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white px-2">
                <span className="shrink-0 rounded-md bg-blue-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white">DPDP</span>
                <select
                  aria-label="DPDP section"
                  value={dpdpSection}
                  onChange={(event) => setDpdpSection(event.target.value)}
                  className={`${fieldClass} flex-1`}
                >
                  <option value="">All sections</option>
                  {dpdpSections.map((section) => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </select>
                <label className="relative w-28 shrink-0">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    value={dpdpQuery}
                    onChange={(event) => setDpdpQuery(event.target.value)}
                    placeholder="Search"
                    className={`${fieldClass} w-full pl-7`}
                  />
                </label>
                <span className="shrink-0 text-[11px] font-bold text-slate-400">{visibleDpdp.length}</span>
              </div>
              <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto bg-slate-50/70 p-1.5">
                {visibleDpdp.length === 0 ? (
                  <p className="px-3 py-8 text-center text-xs text-slate-500">No matching DPDP questions.</p>
                ) : visibleDpdp.map((question, index) => {
                  const active = questionKey(question) === masterId;
                  const mappedCount = mappedMasterCounts[questionKey(question)] || 0;
                  const sectionName = question.section || "General";
                  const previous = index > 0 ? (visibleDpdp[index - 1].section || "General") : "";
                  const showSection = !dpdpSection && sectionName !== previous;
                  return (
                    <div key={questionKey(question)}>
                      {showSection && (
                        <div className="sticky top-0 z-[1] bg-slate-100/95 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          {sectionName}
                        </div>
                      )}
                    <div
                      className={`group flex w-full items-center gap-2 rounded-lg border px-2 py-1 transition ${
                        active
                          ? "border-blue-300 bg-blue-50 shadow-sm ring-2 ring-blue-100"
                          : mappedCount
                            ? "border-emerald-200 bg-emerald-50 hover:border-emerald-300"
                            : "border-transparent bg-white hover:border-slate-200 hover:shadow-sm"
                      }`}
                    >
                      <button
                        type="button"
                        aria-label="Select DPDP question"
                        onClick={() => {
                          setMasterId(questionKey(question));
                          setHint("");
                        }}
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                          active ? "border-blue-600" : "border-slate-300 bg-white"
                        }`}
                      >
                        {active && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                      </button>
                      {question.ref && (
                        <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold ring-1 ${GAP_BADGE}`}>{question.ref}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setReading({
                          kind: "dpdp",
                          id: questionKey(question),
                          title: question.title,
                          ref: question.ref || "",
                          section: question.section || "",
                        })}
                        className={`min-w-0 flex-1 truncate text-left text-[13px] leading-5 ${
                          active ? "font-semibold text-blue-950" : mappedCount ? "font-semibold text-emerald-950" : "font-medium text-slate-800"
                        }`}
                      >
                        {question.title}
                      </button>
                      {mappedCount > 0 && (
                        <span
                          title={`Mapped ${mappedCount} ${mappedCount === 1 ? "time" : "times"}`}
                          className="shrink-0 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                        >
                          {mappedCount}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label="Read full question"
                        onClick={() => setReading({
                          kind: "dpdp",
                          id: questionKey(question),
                          title: question.title,
                          ref: question.ref || "",
                          section: question.section || "",
                        })}
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 opacity-0 transition hover:bg-white hover:text-blue-600 group-hover:opacity-100"
                      >
                        <Expand className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {shownCountries.length === 0 ? (
              <div className="flex min-h-0 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white text-center">
                <ShieldCheck className="h-6 w-6 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Upload a country checklist to start mapping.</p>
              </div>
            ) : shownCountries.map((entry) => {
                const query = (countryQuery[entry.key] || "").trim().toLowerCase();
                const matched = entry.items.filter((item) => {
                  if (entry.section && itemSection(item) !== entry.section) return false;
                  if (!query) return true;
                  return `${itemSection(item)} ${officialRef(item)} ${item.title || ""}`.toLowerCase().includes(query);
                });
                const rows = entry.section ? matched : [...matched].sort((left, right) => {
                  const leftIndex = entry.sections.indexOf(itemSection(left));
                  const rightIndex = entry.sections.indexOf(itemSection(right));
                  return (leftIndex < 0 ? 999 : leftIndex) - (rightIndex < 0 ? 999 : rightIndex);
                });
                const linkedHere = Object.values(entry.links).reduce((sum, list) => sum + list.length, 0);
                return (
                  <section key={entry.key} className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="shrink-0 space-y-1 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="max-w-[96px] shrink-0 truncate rounded-md bg-slate-900 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white" title={entry.country}>{entry.country}</span>
                        {entry.locked ? (
                          <button
                            type="button"
                            onClick={() => startRemap(entry.key)}
                            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-1 text-[10px] font-bold text-blue-700 hover:bg-blue-50"
                          >
                            <RefreshCw className="h-3 w-3" />
                            Remap
                          </button>
                        ) : entry.saved ? (
                          <span className="shrink-0 text-[10px] font-semibold text-amber-700">Editing</span>
                        ) : null}
                        {entry.saved && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => clearCountry(entry)}
                            className="inline-flex shrink-0 items-center rounded-md border border-rose-200 bg-white px-2 py-1 text-[10px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                          >
                            Clear
                          </button>
                        )}
                        {linkedHere > 0 && (
                          <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">{linkedHere}</span>
                        )}
                        <input
                          value={entry.lawName}
                          disabled={entry.locked}
                          onChange={(event) => setCountryField(entry.key, "lawName", event.target.value)}
                          placeholder="Law name"
                          aria-label={`${entry.country} law name`}
                          className={`${fieldClass} min-w-0 flex-1 disabled:bg-slate-50`}
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <select
                          aria-label={`${entry.country} section`}
                          value={entry.section}
                          onChange={(event) => setCountrySection(entry.key, event.target.value)}
                          className={`${fieldClass} flex-1`}
                        >
                          <option value="">All sections</option>
                          {entry.sections.map((section) => (
                            <option key={section} value={section}>{section}</option>
                          ))}
                        </select>
                        <label className="relative w-32 shrink-0">
                          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                          <input
                            value={countryQuery[entry.key] || ""}
                            onChange={(event) => setCountryQuery((current) => ({ ...current, [entry.key]: event.target.value }))}
                            placeholder="Search"
                            className={`${fieldClass} w-full pl-7`}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto bg-slate-50/70 p-1.5">
                      {rows.length === 0 ? (
                        <p className="px-3 py-8 text-center text-xs text-slate-500">No matching questions.</p>
                      ) : rows.map((item, index) => {
                        const checked = isLinked(entry, item);
                        const count = (entry.links[item.id] || []).length;
                        const ref = officialRef(item);
                        const sectionName = itemSection(item);
                        const previous = index > 0 ? itemSection(rows[index - 1]) : "";
                        const showSection = !entry.section && sectionName !== previous;
                        return (
                          <div key={item.id}>
                            {showSection && (
                              <div className="sticky top-0 z-[1] bg-slate-100/95 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                {sectionName}
                              </div>
                            )}
                          <div
                            className={`group flex w-full items-center gap-2 rounded-lg border px-2 py-1 transition ${
                              checked
                                ? "border-blue-300 bg-blue-50 shadow-sm ring-2 ring-blue-100"
                                : "border-transparent bg-white hover:border-slate-200 hover:shadow-sm"
                            }`}
                          >
                            <button
                              type="button"
                              aria-label="Match question"
                              disabled={entry.locked}
                              onClick={() => toggleMatch(entry, item)}
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-md border disabled:cursor-default ${
                                checked ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-transparent"
                              }`}
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            {ref && (
                              <span className={`max-w-[40%] shrink truncate rounded-md px-1.5 py-0.5 text-[10px] font-bold ring-1 ${GAP_BADGE}`}>{ref}</span>
                            )}
                            <button
                              type="button"
                              onClick={() => openCountryReading(entry, item)}
                              className={`min-w-0 flex-1 truncate text-left text-[13px] leading-5 ${checked ? "font-semibold text-blue-950" : "font-medium text-slate-800"}`}
                            >
                              {item.title}
                            </button>
                            {count > 0 && (
                              <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-100">{count}</span>
                            )}
                            <button
                              type="button"
                              aria-label="Read full question"
                              onClick={() => openCountryReading(entry, item)}
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 opacity-0 transition hover:bg-white hover:text-blue-600 group-hover:opacity-100"
                            >
                              <Expand className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
          </div>
        )}

        {reading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm">
            <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <p className="text-[11px] font-bold uppercase tracking-wide text-blue-700">
                {reading.kind === "dpdp" ? "DPDP" : reading.country}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {reading.code && reading.code !== reading.section && (
                  <span className="text-[11px] font-bold text-slate-400">{reading.code}</span>
                )}
                {reading.section && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{reading.section}</span>
                )}
                {reading.category && reading.category.toLowerCase() !== "general" && reading.category !== reading.section && (
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700">{reading.category}</span>
                )}
                {reading.ref && (
                  <span className={`rounded-md px-2 py-1 text-[11px] font-bold ring-1 ${GAP_BADGE}`}>{reading.ref}</span>
                )}
              </div>
              <p className="mt-4 text-base font-semibold leading-7 text-slate-900">{reading.title}</p>
              {[["Evidence", reading.evidence], ["Owner", reading.owner], ["Priority", reading.priority], ["Status", reading.status], ["Notes", reading.notes]]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <p key={label} className="mt-2 text-sm leading-6 text-slate-600">
                    <span className="font-semibold text-slate-800">{label}: </span>{value}
                  </p>
                ))}
              {reading.kind === "country" && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Mapped DPDP questions</p>
                  {readingMapped.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-500">Not mapped yet.</p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {readingMapped.map((question) => (
                        <li key={questionKey(question)} className="text-sm leading-6 text-slate-800">
                          {question.section && <span className="mr-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{question.section}</span>}
                          {question.ref && <span className="mr-2 font-semibold text-blue-700">{question.ref}</span>}
                          {question.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="mt-6 flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setReading(null)}>Close</Button>
                {reading.kind === "dpdp" ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      setMasterId(reading.id);
                      setHint("");
                      setReading(null);
                    }}
                  >
                    Select this question
                  </Button>
                ) : reading.locked ? (
                  <Button type="button" variant="primary" onClick={() => startRemap(reading.countryKey)}>
                    Remap
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      if (readingEntry && readingItem) toggleMatch(readingEntry, readingItem);
                      setReading(null);
                    }}
                  >
                    {readingLinked ? "Remove match" : "Match this question"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {confirming && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-slate-900">Save these country mappings?</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {saveSummary(countriesToSave)} You can map a country again later.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <Button type="button" variant="secondary" disabled={saving} onClick={() => setConfirming(false)}>Go back</Button>
                <Button type="button" variant="primary" loading={saving} onClick={save}>Save mapping</Button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
